import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AuthCallback = () => {
  const { processGoogleCallback, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = location.hash || window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          toast.error('Invalid callback - no session ID');
          navigate('/login');
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        // Process the Google OAuth session
        const userData = await processGoogleCallback(sessionId);
        
        toast.success(`Welcome, ${userData.name}!`);
        
        // Small delay to ensure cookie is set and state is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Re-check auth to ensure cookie is working
        await checkAuth();
        
        // Navigate based on whether user has organization and role
        if (userData.organization_id) {
          // Navigate directly to the role-specific dashboard
          if (userData.role === 'ADMIN') {
            window.location.href = '/admin';
          } else {
            window.location.href = '/department';
          }
        } else {
          window.location.href = '/setup';
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-blue-900/10 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-blue-600" />
          </div>
          <span className="text-2xl font-bold text-gray-900 font-['Outfit']">FinOrchest</span>
        </div>
        
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          <span className="text-gray-500">Completing sign in...</span>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
